import React, { useState, useEffect, useRef, useCallback } from 'react';
import { IonContent, IonPage, IonSpinner } from '@ionic/react';
import { useLocation } from 'react-router-dom';
import { searchEvents, EventWithDistance, formatDistance } from '../services/searchService';
import { useTranslation } from '../hooks/useTranslation';
import PublicEventDetailModal from '../components/PublicEventDetailModal';
import './SearchPage.css';

const PAGE_SIZE = 10;
const RADIUS_OPTIONS: (number | null)[] = [5, 10, 20, 50, null];

const SearchPage: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();

  const getLangFromPath = () => {
    const p = location.pathname;
    if (p.startsWith('/en/')) return 'en';
    if (p.startsWith('/pt-BR/')) return 'pt';
    return 'es';
  };
  const pathLang = getLangFromPath();

  const params = new URLSearchParams(location.search);
  const initialQuery  = params.get('q') || '';
  const initialLat    = parseFloat(params.get('lat') || '') || undefined;
  const initialLng    = parseFloat(params.get('lng') || '') || undefined;
  const [searchQuery, setSearchQuery]     = useState(initialQuery);
  const [inputValue, setInputValue]       = useState(initialQuery);
  const [userLat, setUserLat]             = useState<number | undefined>(initialLat);
  const [userLng, setUserLng]             = useState<number | undefined>(initialLng);
  const [radius, setRadius]               = useState<number | null>(null);
  const [allResults, setAllResults]       = useState<EventWithDistance[]>([]);
  const [displayCount, setDisplayCount]   = useState(PAGE_SIZE);
  const [loading, setLoading]             = useState(false);
  const [isGlobal, setIsGlobal]           = useState(false);
  const [locating, setLocating]           = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventWithDistance | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runSearch = useCallback(async (q: string, lat?: number, lng?: number, r?: number | null) => {
    setLoading(true);
    setDisplayCount(PAGE_SIZE);
    try {
      const effectiveRadius = r !== undefined ? r : radius;
      const { events, isGlobal: global } = await searchEvents(q, lat, lng, effectiveRadius);
      setAllResults(events);
      setIsGlobal(global);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  }, [radius]);

  // Solicitar ubicación automáticamente al entrar
  useEffect(() => {
    if (!navigator.geolocation || initialLat) return;
    navigator.geolocation.getCurrentPosition(
      pos => {
        setUserLat(pos.coords.latitude);
        setUserLng(pos.coords.longitude);
      },
      () => { /* silencioso si el usuario niega */ },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, []); // eslint-disable-line

  // Initial search on mount — carga todos por defecto
  useEffect(() => {
    runSearch(initialQuery, initialLat, initialLng, null);
  }, []); // eslint-disable-line

  // Debounced search on query/radius change
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runSearch(searchQuery, userLat, userLng, radius);
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchQuery, radius, userLat, userLng, runSearch]);

  const handleInputChange = (value: string) => {
    setInputValue(value);
    setSearchQuery(value);
  };

  const handleUseLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      pos => {
        setUserLat(pos.coords.latitude);
        setUserLng(pos.coords.longitude);
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const homeUrl = pathLang === 'en' ? '/en/' : pathLang === 'pt' ? '/pt-BR/' : '/es/';
  const backLabel = pathLang === 'en' ? '← Home' : pathLang === 'pt' ? '← Início' : '← Inicio';

  const displayed = allResults.slice(0, displayCount);
  const hasMore = displayCount < allResults.length;

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  };

  return (
    <IonPage>
      <IonContent className="search-content">
        <div className="search-page">

          {/* Sticky header */}
          <div className="search-header">
            <a href={homeUrl} className="search-back-link">{backLabel}</a>

            <div className="search-bar-row">
              <input
                className="search-input"
                type="text"
                placeholder={t('search.placeholder')}
                value={inputValue}
                onChange={e => handleInputChange(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && runSearch(searchQuery, userLat, userLng)}
              />
              <button
                className="search-btn"
                onClick={() => runSearch(searchQuery, userLat, userLng)}
              >
                {t('search.btn')}
              </button>
            </div>

            <div className="search-controls">
              <button
                className={`search-location-btn ${userLat ? 'search-location-btn--active' : ''}`}
                onClick={handleUseLocation}
                disabled={locating}
              >
                {locating
                  ? <IonSpinner name="crescent" className="search-location-spinner" />
                  : '📍'
                }
                {userLat
                  ? t('search.location_set')
                  : t('search.use_location')
                }
              </button>

              <div className="search-radius-chips">
                {RADIUS_OPTIONS.map(r => (
                  <button
                    key={r ?? 'all'}
                    className={`search-radius-chip ${radius === r ? 'search-radius-chip--active' : ''}`}
                    onClick={() => setRadius(r)}
                  >
                    {r === null ? t('search.radius_all') : `${r} km`}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="search-results">
            {loading ? (
              <div className="search-loading">
                <IonSpinner name="crescent" />
                <p>{t('search.loading')}</p>
              </div>
            ) : (
              <>
                {isGlobal && (
                  <div className="search-global-notice">
                    {t('search.global_fallback')}
                  </div>
                )}

                {allResults.length === 0 ? (
                  <div className="search-empty">
                    <svg className="search-empty-illustration" viewBox="0 0 160 160" fill="none" xmlns="http://www.w3.org/2000/svg">
                      {/* Calendar body */}
                      <rect x="14" y="28" width="112" height="108" rx="10" fill="#EDF2FA" stroke="#2271b1" strokeWidth="2.5"/>
                      {/* Calendar header */}
                      <rect x="14" y="28" width="112" height="34" rx="10" fill="#2271b1"/>
                      <rect x="14" y="48" width="112" height="14" fill="#2271b1"/>
                      {/* Rings */}
                      <rect x="44" y="16" width="8" height="22" rx="4" fill="#1a5a94"/>
                      <rect x="108" y="16" width="8" height="22" rx="4" fill="#1a5a94"/>
                      {/* Header text dots */}
                      <circle cx="56" cy="38" r="3" fill="rgba(255,255,255,0.5)"/>
                      <circle cx="80" cy="38" r="3" fill="rgba(255,255,255,0.5)"/>
                      <circle cx="104" cy="38" r="3" fill="rgba(255,255,255,0.5)"/>
                      {/* Empty rows */}
                      <rect x="28" y="76" width="36" height="6" rx="3" fill="#C3C4C7"/>
                      <rect x="72" y="76" width="36" height="6" rx="3" fill="#C3C4C7"/>
                      <rect x="28" y="96" width="36" height="6" rx="3" fill="#C3C4C7"/>
                      <rect x="72" y="96" width="36" height="6" rx="3" fill="#C3C4C7"/>
                      <rect x="28" y="116" width="22" height="6" rx="3" fill="#E0E1E3"/>
                      <rect x="72" y="116" width="22" height="6" rx="3" fill="#E0E1E3"/>
                      {/* Magnifying glass */}
                      <circle cx="118" cy="118" r="20" fill="white" stroke="#E0E1E3" strokeWidth="1.5"/>
                      <circle cx="113" cy="113" r="10" stroke="#2271b1" strokeWidth="2.5"/>
                      <line x1="120" y1="120" x2="130" y2="130" stroke="#2271b1" strokeWidth="3" strokeLinecap="round"/>
                      {/* X inside magnifier */}
                      <line x1="109" y1="109" x2="117" y2="117" stroke="#2271b1" strokeWidth="2" strokeLinecap="round"/>
                      <line x1="117" y1="109" x2="109" y2="117" stroke="#2271b1" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    <h3 className="search-empty-title">{t('search.no_activities')}</h3>
                    <p className="search-empty-desc">{searchQuery ? t('search.no_results_desc') : t('search.no_activities_desc')}</p>
                  </div>
                ) : (
                  <>
                    <p className="search-count">
                      {t('search.results_count', { count: String(allResults.length) })}
                    </p>

                    <div className="search-list">
                      {displayed.map(event => (
                        <button
                          key={event.id}
                          className="search-card"
                          onClick={() => setSelectedEvent(event)}
                        >
                          <div className="search-card-photo-wrap">
                            <img
                              src={event.photoItem}
                              alt={event.information}
                              className="search-card-photo"
                            />
                          </div>
                          <div className="search-card-info">
                            <h3 className="search-card-title">{event.information}</h3>
                            <p className="search-card-meta">
                              📅 {formatDate(event.date)} · {event.schedule_start}
                            </p>
                            <p className="search-card-address">
                              📍 {event.address_id}
                            </p>
                            <div className="search-card-badges">
                              <span className={`search-badge search-badge--price ${event.price === 0 ? 'search-badge--free' : ''}`}>
                                {event.price === 0 ? t('search.free') : `${event.currency ?? ''}${Number(event.price).toFixed(2)}`}
                              </span>
                              {event.distanceKm > 0 && (
                                <span className="search-badge search-badge--distance">
                                  📏 {formatDistance(event.distanceKm)}
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>

                    {hasMore && (
                      <button
                        className="search-load-more"
                        onClick={() => setDisplayCount(c => c + PAGE_SIZE)}
                      >
                        {t('search.load_more')}
                      </button>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {selectedEvent && (
          <PublicEventDetailModal
            event={selectedEvent}
            onClose={() => setSelectedEvent(null)}
          />
        )}
      </IonContent>
    </IonPage>
  );
};

export default SearchPage;
